import React from "react";
import {
  Avatar,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  CongressionalTrade,
  filingUrl,
  isMemberTrade,
  tradeDate,
  tradeDirection,
  tradeMemberName,
} from "../congressionalTradeData";
import { congressionalMembers, createMemberSlug } from "../congressionalConfig";
import { CompanyIdentity, companyCellSx } from "../../shared/CompanyIdentity";
import { investorTableSx } from "./InvestorWorkspace";
import { getPersonPortraitImageProps } from "../personPortrait";

interface CongressionalTradeTableProps {
  trades: CongressionalTrade[];
  showMember?: boolean;
}
export const CongressionalTradeTable = ({
  trades,
  showMember = true,
}: CongressionalTradeTableProps) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const tabletWidths = showMember
    ? { asset: "27%", dates: "17%", activity: "14%", value: "22%" }
    : { asset: "41%", dates: "20%", activity: "16%", value: "23%" };
  const assetWidth = isTablet ? tabletWidths.asset : showMember ? "26%" : "40%";
  const activityWidth = isTablet ? tabletWidths.activity : undefined;
  const valueWidth = isTablet ? tabletWidths.value : "1px";
  const tableWhiteSpace = isTablet ? "normal" : "nowrap";
  const direction = (trade: CongressionalTrade) => {
    const kind = tradeDirection(trade.type);
    const color =
      kind === "purchase"
        ? "primary.main"
        : kind === "sale"
          ? "error.main"
          : "text.secondary";
    return (
      <Typography
        component="span"
        sx={{
          color,
          fontSize: "0.8125rem",
          whiteSpace: tableWhiteSpace,
        }}
      >
        {trade.type ?? "Other"}
      </Typography>
    );
  };
  const asset = (trade: CongressionalTrade) => (
    <CompanyIdentity
      company={trade.assetDescription}
      symbol={trade.symbol}
      metadata={trade.assetType}
    />
  );
  const member = (trade: CongressionalTrade) => {
    const profile = congressionalMembers.find((item) =>
      isMemberTrade(trade, item.fmpName),
    );
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {showMember && profile && (
          <Avatar
            {...getPersonPortraitImageProps(profile.imageUrl, 28)}
            alt=""
            sx={{ width: 28, height: 28, borderRadius: 1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {showMember && profile && (
            <Link
              component={RouterLink}
              to={`/super-investors/congressional/${createMemberSlug(profile.name)}`}
              underline="hover"
              title={tradeMemberName(trade)}
              sx={{
                color: "text.primary",
                fontSize: "0.8125rem",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: tableWhiteSpace,
              }}
            >
              {tradeMemberName(trade)}
            </Link>
          )}
          {showMember && !profile && (
            <Typography noWrap={!isTablet} sx={{ fontSize: "0.8125rem" }}>
              {tradeMemberName(trade)}
            </Typography>
          )}
          <Typography
            sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 }}
          >
            {trade.chamber === "house" && "House"}
            {trade.chamber === "senate" && "Senate"}
          </Typography>
        </Box>
      </Box>
    );
  };
  const disclosure = (trade: CongressionalTrade) => {
    const href = filingUrl(trade.link);
    return (
      <>
        {href && (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            aria-label={`Open filing disclosed ${tradeDate(trade.disclosureDate)}`}
            sx={{
              color: "text.secondary",
              fontSize: "0.8125rem",
              textDecoration: "underline",
              textDecorationColor: "divider",
              textUnderlineOffset: "3px",
            }}
          >
            {tradeDate(trade.disclosureDate)}
          </Link>
        )}
        {!href && (
          <Typography
            component="span"
            sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
          >
            {tradeDate(trade.disclosureDate)}
          </Typography>
        )}
      </>
    );
  };
  const key = (trade: CongressionalTrade, index: number) =>
    `${trade.link}-${trade.symbol}-${trade.transactionDate}-${index}`;
  if (!trades.length)
    return (
      <Typography
        role="status"
        color="text.secondary"
        sx={{ py: 5, textAlign: "center", fontSize: "0.8125rem" }}
      >
        No disclosures available.
      </Typography>
    );
  if (compact)
    return (
      <Box
        component="ul"
        aria-label="Trading disclosures"
        sx={{ p: 0, m: 0, listStyle: "none" }}
      >
        {trades.map((trade, index) => (
          <Box
            component="li"
            key={key(trade, index)}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "minmax(0, 1fr) auto",
              },
              columnGap: 2,
              p: { xs: 1, sm: 1.5 },
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              {asset(trade)}
              {direction(trade)}
            </Box>
            {showMember && (
              <Box sx={{ mt: 1, gridColumn: 1 }}>{member(trade)}</Box>
            )}
            <Box
              sx={{
                gridColumn: 1,
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 1,
              }}
            >
              <Box>
                <Typography className="disclosure-label">Trade date</Typography>
                <Typography
                  sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                >
                  {tradeDate(trade.transactionDate)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography className="disclosure-label">
                  Disclosed / filing
                </Typography>
                {disclosure(trade)}
              </Box>
            </Box>
            <Box
              sx={{
                mt: 1,
                textAlign: "right",
                gridColumn: { xs: 1, sm: 2 },
                gridRow: { sm: "2 / 4" },
                alignSelf: "end",
              }}
            >
              <Typography className="disclosure-label">
                Disclosed value
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  letterSpacing: "-0.025em",
                }}
              >
                {trade.amount ?? "Not disclosed"}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        aria-label="Trading disclosures"
        sx={{
          ...investorTableSx(theme),
          [theme.breakpoints.between("sm", "lg")]: {
            tableLayout: "fixed",
            minWidth: 600,
            "& .MuiTableCell-root": { px: 1 },
            "& .MuiTableCell-head": {
              fontSize: "0.75rem",
              letterSpacing: "0.025em",
              whiteSpace: "normal",
            },
            "& tbody .trade-value": { whiteSpace: "normal" },
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: assetWidth }}>Asset</TableCell>
            {showMember && <TableCell sx={{ width: "20%" }}>Member</TableCell>}
            {isTablet && (
              <TableCell sx={{ width: tabletWidths.dates }}>
                Trade / disclosed
              </TableCell>
            )}
            {!isTablet && <TableCell>Trade date</TableCell>}
            {!isTablet && <TableCell>Disclosed / filing</TableCell>}
            <TableCell sx={{ width: activityWidth }}>Transaction</TableCell>
            <TableCell
              className="trade-value"
              sx={{ width: valueWidth, whiteSpace: "nowrap" }}
            >
              Disclosed value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trades.map((trade, index) => (
            <TableRow key={key(trade, index)}>
              <TableCell sx={companyCellSx}>{asset(trade)}</TableCell>
              {showMember && (
                <TableCell sx={{ maxWidth: 0 }}>{member(trade)}</TableCell>
              )}
              <TableCell sx={{ color: "text.secondary" }}>
                <Box component="span" title="Trade date">
                  {tradeDate(trade.transactionDate)}
                </Box>
                {isTablet && <Box sx={{ mt: 0.25 }}>{disclosure(trade)}</Box>}
              </TableCell>
              {!isTablet && <TableCell>{disclosure(trade)}</TableCell>}
              <TableCell>{direction(trade)}</TableCell>
              <TableCell className="trade-value">
                {trade.amount ?? "Not disclosed"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
